import { Alert, Box, Divider, Grid, Typography } from '@mui/material'
import { Category, categoryFromJSON, GlobalNotificationProto, GlobalNotificationType } from '../../../proto/generated/global-notification'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { selectGlobalNotifications } from '../../../selectors/globalNotification'
import EmptyGlobalNotificationView from '../EmptyGlobalNotificationView/EmptyGlobalNotificationView'
import { useStyles } from './styles'

const CATEGORY_TO_SEVERITY: Record<Category, 'info' | 'warning' | 'error'> = {
    [Category.CATEGORY_UNKNOWN]: 'info',
    [Category.INFO]: 'info',
    [Category.WARN]: 'warning',
    [Category.ERROR]: 'error',
    [Category.UNRECOGNIZED]: 'info'
}

interface NotificationItemProps {
    notification: GlobalNotificationProto & { [key: string]: any }
}

const NotificationItem = ({ notification }: NotificationItemProps) => {
    const [expanded, setExpanded] = useState(false)
    const [isClamped, setIsClamped] = useState(false)
    const textRef = useRef<HTMLSpanElement>(null)
    const { classes } = useStyles()

    const categoryEnum = categoryFromJSON(notification.category)
    const severity = CATEGORY_TO_SEVERITY[categoryEnum] || 'info'

    useEffect(() => {
        const el = textRef.current
        if (el) {
            setIsClamped(el.scrollHeight > el.clientHeight)
        }
    }, [notification.message])

    return (
        <Grid item xs={12} key={notification.id} padding={0}>
            <Alert severity={severity} sx={{ color: 'inherit', background: 'none', boxShadow: 'none', '& .MuiAlert-icon': { marginRight: '7px' } }}>
                <Typography fontWeight={500}>
                    {notification.header}
                </Typography>
                <Typography
                    ref={textRef}
                    component="span"
                    display="block"
                    className={`${classes.notificationText} ${expanded ? classes.expanded : classes.collapsed}`}
                >
                    {notification.message}
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {isClamped && !expanded && (
                        <Typography
                            component="span"
                            color="inherit"
                            fontSize={'0.75rem'}
                            sx={{ cursor: 'pointer', fontWeight: 500 }}
                            onClick={() => setExpanded(true)}
                        >
                            {' Read more'}
                        </Typography>
                    )}

                    {expanded && (
                        <Typography
                            component="span"
                            color="inherit"
                            fontSize={'0.75rem'}
                            sx={{ cursor: 'pointer', fontWeight: 500 }}
                            onClick={() => setExpanded(false)}
                        >
                            {' Show less'}
                        </Typography>
                    )}
                </Box>

                <span style={{ fontSize: '0.75rem' }}>{dayjs(notification.updatedAt).format('DD-MM-YYYY HH:mm A')}</span>
            </Alert>
            <Divider />
        </Grid>
    )
}

const SystemNotifications = () => {
    const notifications: GlobalNotificationProto[] = useSelector(selectGlobalNotifications)

    const systemNotifications = notifications.filter((notification) => notification.type === GlobalNotificationType.SYSTEM_NOTIFICATION && notification.enabled).sort((a, b) => b.updatedAt - a.updatedAt)

    return (
        <Grid container sx={{ flex: systemNotifications.length > 0 ? 0 : 1, display: 'flex', flexDirection: 'row' }}>
            {systemNotifications.length > 0 ? (
                systemNotifications.map((notification: any) => (
                    <NotificationItem key={notification.id} notification={notification} />
                ))
            ) : (
                <Grid container sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <EmptyGlobalNotificationView />
                    </Box>
                </Grid>
            )}
        </Grid>
    )
}

export default SystemNotifications
